import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

// Connects to data-controller="notifications"
export default class extends Controller {
  static targets = ["container"]

  connect() {
    this.subscribeToChannel()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  subscribeToChannel() {
    // Get emulated user ID from meta tag if present
    const emulatedUserMeta = document.querySelector('meta[name="emulated-user-id"]')
    const emulatedUserId = emulatedUserMeta ? emulatedUserMeta.content : null

    // Create consumer with emulated user ID in URL if present
    let consumerUrl = null
    if (emulatedUserId) {
      const cableUrl = document.querySelector('meta[name="action-cable-url"]')?.content || '/cable'
      consumerUrl = `${cableUrl}?emulated_user_id=${emulatedUserId}`
    }

    const consumer = consumerUrl ? createConsumer(consumerUrl) : createConsumer()

    this.subscription = consumer.subscriptions.create(
      { channel: "NotificationsChannel" },
      {
        connected: () => {
          // Connected to notifications
        },
        disconnected: () => {
          // Disconnected from notifications
        },
        received: (data) => {
          this.handleNotification(data)
        }
      }
    )
  }

  handleNotification(data) {
    const { type, message, url } = data

    // Create notification element
    const notification = this.createNotificationElement(type, message, url)

    // Add to container
    if (this.hasContainerTarget) {
      this.containerTarget.appendChild(notification)
    } else {
      // If no container, append to body
      document.body.appendChild(notification)
    }

    // Auto-dismiss after 10 seconds
    setTimeout(() => {
      this.dismissNotification(notification)
    }, 10000)

    // If on the same page as the notification URL, reload the page
    if (url && window.location.pathname === new URL(url, window.location.origin).pathname) {
      setTimeout(() => {
        window.location.reload()
      }, 2000) // Wait 2 seconds so user sees the notification
    }
  }  createNotificationElement(type, message, url) {
    const div = document.createElement('div')
    div.className = `fixed top-4 right-4 max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden z-50 notification-slide-in`

    const colors = {
      success: { bg: 'bg-green-50', border: 'border-green-200', icon: 'text-green-400' },
      error: { bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-400' },
      info: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-400' },
      warning: { bg: 'bg-yellow-50', border: 'border-yellow-200', icon: 'text-yellow-400' }
    }

    const color = colors[type] || colors.info

    div.innerHTML = `
      <div class="p-4">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            ${this.getIconForType(type, color.icon)}
          </div>
          <div class="ml-3 w-0 flex-1 pt-0.5">
            <p class="text-sm font-medium text-gray-900">${message}</p>
            ${url ? `
              <div class="mt-3">
                <a href="${url}" class="text-sm font-medium text-indigo-600 hover:text-indigo-500">
                  View details →
                </a>
              </div>
            ` : ''}
          </div>
          <div class="ml-4 flex-shrink-0 flex">
            <button type="button" data-action="click->notifications#dismiss" class="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
              <span class="sr-only">Close</span>
              <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    `

    return div
  }

  getIconForType(type, colorClass) {
    const icons = {
      success: `<svg class="h-6 w-6 ${colorClass}" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>`,
      error: `<svg class="h-6 w-6 ${colorClass}" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>`,
      info: `<svg class="h-6 w-6 ${colorClass}" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>`,
      warning: `<svg class="h-6 w-6 ${colorClass}" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>`
    }

    return icons[type] || icons.info
  }

  dismiss(event) {
    const notification = event.currentTarget.closest('.notification-slide-in')
    this.dismissNotification(notification)
  }

  dismissNotification(notification) {
    notification.classList.add('notification-slide-out')
    setTimeout(() => {
      notification.remove()
    }, 300)
  }
}
